import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Layers, ArrowLeft, ArrowRight } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import {
  buildPlanPayload,
  CATEGORY_DEFS,
  CATEGORY_KEYS,
  computeMultiToothFee,
  crownUnitProcedures,
  eligibleMultiToothProcedures,
  procsForCategory,
} from "./multiToothProcedureModel";

// =====================================================================
// BuildMultiToothDialog — locked Multi-Tooth Tool flow (memory:
// project_multi_tooth_tool_flow). Self-contained dialog: pick adjacent
// teeth → palette filters to multi-tooth procedures → optional role
// marker for Bridge / role-required procedures → calls
// public.add_procedure_to_plan with the teeth jsonb.
//
// Adjacency rule mirrors dos_v2._fdi_are_adjacent (server-side double-
// check still runs). Client-side validation gives instant UX feedback;
// server is the source of truth.
// =====================================================================

// FDI ordinal-position math, identical to the server helper.
function fdiToArch(fdi) {
  const q = Math.floor(Number(fdi) / 10);
  if (q === 1 || q === 2) return "upper";
  if (q === 3 || q === 4) return "lower";
  return null;
}
function fdiToPosition(fdi) {
  const num = Number(fdi);
  if (!num || num < 11 || num > 48) return null;
  const quad = Math.floor(num / 10);
  const unit = num % 10;
  if (![1,2,3,4].includes(quad) || unit < 1 || unit > 8) return null;
  return (quad === 1 || quad === 4) ? (8 - unit) : (7 + unit);
}
function teethAreAdjacent(teeth) {
  if (!Array.isArray(teeth) || teeth.length <= 1) return true;
  const archs = teeth.map(fdiToArch);
  if (archs.includes(null)) return false;
  if (new Set(archs).size > 1) return false;
  const positions = teeth.map(fdiToPosition);
  if (positions.includes(null)) return false;
  if (new Set(positions).size !== positions.length) return false; // dupes
  const sorted = [...positions].sort((a, b) => a - b);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] !== sorted[i-1] + 1) return false;
  }
  return true;
}

// Default role assignment.
//
// Primary signal — chart truth: any tooth flagged Missing on the
// latest finalized chart is automatically a pontic (you cannot place
// a crown on air). Standing teeth default to abutment.
//
// Fallback when no missing-set is provided OR no teeth in the span are
// flagged missing: position-based heuristic (ends = abutment, middle
// = pontic). This keeps splints sensible (all teeth standing → ends
// anchor) and lets the dialog work even before a chart exists.
//
// Doctor can flip any tooth with one click. We just give them the
// clinically-correct default so the common case is zero-effort.
function defaultRoles(sortedFdis, missingFdiSet) {
  if (sortedFdis.length < 2) return {};
  const out = {};
  const missing = missingFdiSet instanceof Set ? missingFdiSet : null;
  const anyMissing = missing && sortedFdis.some((f) => missing.has(String(f)));

  if (anyMissing) {
    // Chart-truth path: missing → pontic, standing → abutment.
    sortedFdis.forEach((fdi) => {
      out[fdi] = missing.has(String(fdi)) ? "pontic" : "abutment";
    });
    return out;
  }

  // Position-based fallback (no missing teeth in the span — likely a
  // splint, or a planned-extraction-then-bridge case the doctor will
  // mark up manually).
  sortedFdis.forEach((fdi, i) => {
    out[fdi] = (i === 0 || i === sortedFdis.length - 1) ? "abutment" : "pontic";
  });
  if (sortedFdis.length === 2) {
    // 2-tooth span has no "middle" — force one pontic so Bridge
    // validation passes. Doctor flips if it's actually a 2-unit splint.
    out[sortedFdis[1]] = "pontic";
  }
  return out;
}

// FDI rows for the mini picker.
const UPPER_FDIS = ["18","17","16","15","14","13","12","11","21","22","23","24","25","26","27","28"];
const LOWER_FDIS = ["48","47","46","45","44","43","42","41","31","32","33","34","35","36","37","38"];

function formatPHP(centavos) {
  if (!Number.isFinite(centavos)) return "—";
  return `₱${Math.round(centavos / 100).toLocaleString()}`;
}

export default function BuildMultiToothDialog({
  open, onOpenChange, planId, procedures = [], presetTeeth = null,
  missingFdiSet = null,   // Set<string> — FDIs flagged Missing on the chart
  onEnsureDraftPlan,      // optional async () => plan id; used by Doctor chairside auto-start/revise flow
  onAdded,
}) {
  // Top-level filter: grouped crown units, multi-tooth multi-visit
  // procedures (Bridge + Partial), plus Full Denture catalog rows
  // (arch-level — explicitly allowed even though tooth_count='none').
  const eligibleProcs = useMemo(() => {
    return eligibleMultiToothProcedures(procedures);
  }, [procedures]);

  // When presetTeeth is provided (the chart-driven flow), the dialog
  // skips its own tooth picker and uses the chart's selection. Local
  // selectedFdis still tracks selection so legacy entry (no preset)
  // works the same way.
  const usingPreset = Array.isArray(presetTeeth) && presetTeeth.length > 0;
  const [selectedFdis, setSelectedFdis] = useState([]);
  const [categoryKey, setCategoryKey] = useState(null);
  const [pickedProcedure, setPickedProcedure] = useState(null);
  // For Fixed Bridge: which crown type the lab will fabricate per unit.
  // Bridge fee = crownProc.default_fee × teeth.length (units), regardless
  // of abutment/pontic split — clinical reality, not the catalog's
  // legacy "per pontic" framing.
  const [crownProc, setCrownProc] = useState(null);
  const [roles, setRoles] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Reset on open. Pre-load from presetTeeth when chart-driven.
  useEffect(() => {
    if (!open) return;
    setSelectedFdis(usingPreset ? [...presetTeeth] : []);
    setCategoryKey(null);
    setPickedProcedure(null);
    setCrownProc(null);
    setRoles({});
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Crown procedures eligible as Bridge fabrication units.
  // Catalog-driven: procedures.multi_tooth_kind = 'crown_unit'.
  // multiToothKindOf() falls back to legacy regex when the flag is
  // null (pre-migration), so this works in both worlds.
  const crownProcs = useMemo(() => {
    return crownUnitProcedures(procedures);
  }, [procedures]);

  // Procedures within the picked category.
  const procsInCategory = useMemo(() => {
    if (!categoryKey) return [];
    return procsForCategory(eligibleProcs, categoryKey);
  }, [categoryKey, eligibleProcs]);

  // Counts per category, used to show the empty-state hint inline.
  const countByCategory = useMemo(() => {
    const out = {};
    for (const k of CATEGORY_KEYS) {
      out[k] = eligibleProcs.filter(CATEGORY_DEFS[k].matches).length;
    }
    return out;
  }, [eligibleProcs]);

  // Re-default roles whenever selection or proc changes. Missing-tooth
  // chart signal (passed in from TreatmentTab) makes pontic auto-marking
  // clinically correct: a missing tooth must be a pontic, a standing
  // tooth defaults to abutment. Doctor can still flip any tooth.
  useEffect(() => {
    if (!pickedProcedure?.tooth_role_required) {
      setRoles({});
      return;
    }
    const sorted = [...selectedFdis].sort(
      (a, b) => fdiToPosition(a) - fdiToPosition(b),
    );
    setRoles(defaultRoles(sorted, missingFdiSet));
    // missingFdiSet intentionally NOT in the dep array — its identity
    // changes on every TreatmentTab render (useMemo creates a fresh
    // Set), and we only want re-defaulting when the user changes the
    // selection or procedure. The set value at first render is the
    // truth we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFdis, pickedProcedure]);

  // Clear procedure pick when the category changes — keeps the list
  // and downstream state in sync.
  useEffect(() => {
    setPickedProcedure(null);
    setCrownProc(null);
    setRoles({});
    // For Bridge category, auto-pick the Bridge procedure if there's
    // only one (catalog default). Crown type is the real choice the
    // dentist makes for Bridge.
    if (categoryKey === "bridge") {
      const bridgeProcs = eligibleProcs.filter(CATEGORY_DEFS.bridge.matches);
      if (bridgeProcs.length === 1) setPickedProcedure(bridgeProcs[0]);
    }
  }, [categoryKey, eligibleProcs]);

  if (!open) return null;

  const teethCount = selectedFdis.length;
  const adjacent   = teethAreAdjacent(selectedFdis);

  // --- Clinical-language helpers (used by header + preview pane) ---

  // Title flips per category — "Build a Bridge" reads better than
  // generic "Build a Multi-Tooth Procedure" once the dentist picks.
  const dialogTitle = categoryKey === "crown_units"     ? "Build Crown Units"
                    : categoryKey === "bridge"          ? "Build a Bridge"
                    : categoryKey === "partial_denture" ? "Build a Partial Denture"
                    : categoryKey === "full_denture"    ? "Build a Full Denture"
                    : "Build a Multi-Tooth Procedure";

  // Arch label from the selected FDIs. "upper-right" / "lower-left" /
  // "upper, midline" / "upper" — used for clinical sentences.
  const archLabel = (() => {
    if (selectedFdis.length === 0) return null;
    const sorted = [...selectedFdis].sort((a, b) => fdiToPosition(a) - fdiToPosition(b));
    const first = sorted[0];
    const arch  = (first / 10 | 0) <= 2 ? "upper" : "lower";
    const positions = sorted.map(fdiToPosition);
    const minP = Math.min(...positions);
    const maxP = Math.max(...positions);
    // Crosses the arch midline (positions 7|8 boundary).
    if (minP <= 7 && maxP >= 8) return arch + ", midline";
    if (maxP <= 7) return arch + "-right";
    return arch + "-left";
  })();

  // FDI-range string for header/preview ("FDI 14–16").
  const fdiRange = (() => {
    if (selectedFdis.length === 0) return null;
    const sorted = [...selectedFdis].sort((a, b) => fdiToPosition(a) - fdiToPosition(b));
    return sorted.length === 1
      ? `FDI ${sorted[0]}`
      : `FDI ${sorted[0]}–${sorted[sorted.length - 1]}`;
  })();
  // Categories always show. Specific procedures and submit gating
  // depend on the category requirement (multi-tooth vs arch-level).
  const showProcs  = true;
  const archLevel  = categoryKey === "full_denture";
  const needsRoles = pickedProcedure?.tooth_role_required === true;
  const ponticCount = needsRoles
    ? selectedFdis.filter((f) => roles[f] === "pontic").length
    : 0;
  const allRoled    = needsRoles
    ? selectedFdis.every((f) => roles[f] === "abutment" || roles[f] === "pontic")
    : true;
  const hasPontic   = !needsRoles || ponticCount >= 1;

  // Header subtitle — precise once a category is picked.
  const headerSubtitle = !categoryKey
    ? "Crowns, Bridge, Partial Denture — pick adjacent teeth, then the procedure."
    : categoryKey === "full_denture"
      ? "Whole-arch — your tooth selection sets the arch, not the span."
      : categoryKey === "crown_units"
        ? `${teethCount} crown unit${teethCount === 1 ? "" : "s"} · ${archLabel || "—"} · ${fdiRange || ""}`
      : `${teethCount} unit${teethCount === 1 ? "" : "s"} · ${archLabel || "—"} · ${fdiRange || ""}`;

  // Fee model:
  //   Fixed Bridge       → crownProc.default_fee × teeth.length (per unit)
  //   Partial / other    → procedure.default_fee_centavos (catalog flat)
  //   Full Denture       → procedure.default_fee_centavos (per arch)
  const computedFee = (() => {
    return computeMultiToothFee({
      categoryKey,
      pickedProcedure,
      crownProc,
      teethCount,
    });
  })();

  // Clinical sentence the dentist would actually say. Lives at the
  // bottom of the preview pane and on the success toast.
  const clinicalSentence = (() => {
    if (!categoryKey) return null;
    if (categoryKey === "crown_units") {
      if (!pickedProcedure) return "Pick the crown type.";
      return `${teethCount}-unit ${pickedProcedure.name}${archLabel ? ` · ${archLabel}` : ""} · ${formatPHP(computedFee)}`;
    }
    if (categoryKey === "bridge") {
      if (!crownProc) return "Pick a crown type to size the Bridge.";
      return `${teethCount}-unit Bridge × ${crownProc.name}${archLabel ? ` · ${archLabel}` : ""} · ${formatPHP(computedFee)}`;
    }
    if (categoryKey === "partial_denture") {
      if (!pickedProcedure) return "Pick the partial denture type.";
      return `${pickedProcedure.name}${archLabel ? ` · ${archLabel}` : ""} · ${teethCount} ${teethCount === 1 ? "tooth" : "teeth"} · ${formatPHP(computedFee)}`;
    }
    if (categoryKey === "full_denture") {
      if (!pickedProcedure) return "Pick the denture type.";
      const archWord = (archLabel || "").startsWith("upper") ? "Upper"
                     : (archLabel || "").startsWith("lower") ? "Lower" : "";
      return `Complete ${archWord} Denture · ${formatPHP(computedFee)}`.replace("  ", " ");
    }
    return null;
  })();

  // Disabled reason for the Add-to-plan button — shown inline so the
  // dentist knows what's missing without trial-and-error.
  const disabledReason = (() => {
    if (submitting) return "Saving…";
    if (!categoryKey) return "Pick the type";
    if (!archLevel && (teethCount < 2 || !adjacent)) return "Pick adjacent teeth on the chart";
    if (categoryKey === "bridge" && !crownProc) return "Pick the crown type";
    if (!pickedProcedure) return "Pick the procedure";
    if (!planId && typeof onEnsureDraftPlan !== "function") return "Start a draft plan first";
    if (needsRoles && (!allRoled || !hasPontic)) return "Mark abutments + pontics";
    return null;
  })();

  function toggleFdi(fdi) {
    setSelectedFdis((prev) => {
      const next = prev.includes(fdi)
        ? prev.filter((x) => x !== fdi)
        : [...prev, fdi];
      // Validate after add; if non-adjacent, revert with a toast.
      if (!teethAreAdjacent(next)) {
        // Allow deselect even if pre-state was non-adjacent.
        if (next.length < prev.length) return next;
        toast.error("Adjacent teeth only — same arch, no gaps.");
        return prev;
      }
      return next;
    });
    // Picking a procedure auto-clears when teeth change so the user
    // re-picks deliberately. Avoids stale fee math.
    setPickedProcedure(null);
  }

  function setRole(fdi, role) {
    setRoles((prev) => ({ ...prev, [fdi]: role }));
  }

  async function handleAdd() {
    if (!pickedProcedure) return;
    const isArchLevel = pickedProcedure.tooth_count === "none";
    if (!isArchLevel && selectedFdis.length < 2) {
      toast.error("Pick at least two adjacent teeth.");
      return;
    }
    if (categoryKey === "bridge" && !crownProc) {
      toast.error("Pick the crown type for the bridge units.");
      return;
    }
    if (needsRoles && (!allRoled || !hasPontic)) {
      toast.error("Mark each tooth as abutment or pontic.");
      return;
    }
    const { procedureId, teeth, material } = buildPlanPayload({
      categoryKey,
      pickedProcedure,
      crownProc,
      selectedFdis,
      roles,
    });
    setSubmitting(true);
    let targetPlanId = planId;
    if (typeof onEnsureDraftPlan === "function") {
      try {
        const ensured = await onEnsureDraftPlan();
        targetPlanId = typeof ensured === "string" ? ensured : ensured?.id;
      } catch (err) {
        setSubmitting(false);
        toast.error(err?.message ?? "Could not start a draft plan.");
        return;
      }
    }
    if (!targetPlanId) {
      setSubmitting(false);
      toast.error("Start a draft plan before adding this procedure.");
      return;
    }
    // p_phase_number stays null so the server defaults to phase 1.
    // Forwarding pickedProcedure.default_phase here is what dropped
    // bridges into phase 2 silently — the catalog's default_phase
    // shouldn't push a multi-visit procedure forward; the receptionist
    // can re-phase explicitly after the item is on the plan.
    const { data, error } = await supabase.rpc("add_procedure_to_plan", {
      p_plan_id:               targetPlanId,
      p_procedure_id:          procedureId,
      p_teeth:                 teeth,
      p_phase_number:          null,
      p_notes:                 null,
      p_material:              material,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message ?? "Could not add procedure to plan.");
      return;
    }
    const summary = categoryKey === "bridge" && crownProc
      ? `${crownProc.name} × ${teethCount} units · ${formatPHP(computedFee)}`
      : categoryKey === "crown_units"
        ? `${pickedProcedure.name} × ${teethCount} units · ${formatPHP(computedFee)}`
      : `${pickedProcedure.name} (${teethCount} teeth · ${formatPHP(computedFee)})`;
    toast.success(`Added ${summary}`);
    onAdded?.(data, targetPlanId);
    onOpenChange?.(false);
  }

  // Render --------------------------------------------------------------
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
         role="dialog" aria-modal="true" data-testid="multi-tooth-dialog">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2">
              <Layers className="w-4 h-4 text-violet-600" />
              {dialogTitle}
            </h2>
            <p className="text-xs text-slate-500 mt-1 truncate">{headerSubtitle}</p>
          </div>
          <button type="button" onClick={() => onOpenChange?.(false)}
                  className="text-slate-400 hover:text-slate-700 shrink-0"
                  aria-label="Close" data-testid="multi-tooth-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 p-5">
          {/* DECISIONS PANE (left, 60% on desktop) */}
          <div className="lg:col-span-3 space-y-4 min-w-0">
          {/* Selected teeth — compact display when chart pre-selected,
              full picker when entered from the workspace path. */}
          {usingPreset ? (
            <section>
              <p className="text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
                Selected teeth
              </p>
              <div className="bg-violet-50 border border-violet-200 rounded-xl px-3 py-2.5 text-sm">
                {selectedFdis.length === 0 ? (
                  <span className="text-slate-500">No teeth selected — close and pick teeth on the chart.</span>
                ) : (
                  <span>
                    {[...selectedFdis]
                      .sort((a, b) => fdiToPosition(a) - fdiToPosition(b))
                      .map((fdi, i, arr) => {
                        const isMissing = missingFdiSet instanceof Set && missingFdiSet.has(String(fdi));
                        return (
                          <span key={fdi}>
                            <span
                              className={`font-semibold tabular-nums ${isMissing ? "line-through decoration-2 decoration-slate-400 text-slate-500" : ""}`}
                              title={isMissing ? "Missing on chart" : undefined}
                            >
                              #{fdi}
                            </span>
                            {isMissing ? (
                              <span className="ml-1 inline-flex items-center text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded bg-slate-200 text-slate-600 align-middle">
                                missing
                              </span>
                            ) : null}
                            {i < arr.length - 1 ? <span className="text-violet-400 mx-1">—</span> : null}
                          </span>
                        );
                      })}
                    <span className="ml-2 text-slate-500">
                      · {teethCount} unit{teethCount === 1 ? "" : "s"}
                      {archLabel ? ` · ${archLabel}` : ""}
                    </span>
                  </span>
                )}
              </div>
            </section>
          ) : (
            <section>
              <p className="text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
                Pick adjacent teeth
              </p>
              <div className="bg-slate-50 rounded-xl p-3 space-y-2">
                <ToothRow fdis={UPPER_FDIS} selected={selectedFdis} onToggle={toggleFdi} />
                <div className="h-px bg-slate-300/50 mx-2" />
                <ToothRow fdis={LOWER_FDIS} selected={selectedFdis} onToggle={toggleFdi} />
              </div>
              <p className="mt-2 text-xs text-slate-600 inline-flex items-center gap-2 flex-wrap">
                {teethCount === 0 ? (
                  <span>No teeth selected yet.</span>
                ) : !adjacent ? (
                  <span className="text-rose-700">Selection isn't a continuous run within one arch.</span>
                ) : (
                  <span>
                    Selected: <span className="font-semibold">{selectedFdis.join(", ")}</span>
                    {" · "}{teethCount} unit{teethCount === 1 ? "" : "s"}
                    {archLabel ? ` · ${archLabel}` : ""}
                  </span>
                )}
                {teethCount > 0 ? (
                  <button type="button" onClick={() => setSelectedFdis([])}
                          data-testid="multi-tooth-clear"
                          className="text-slate-500 underline hover:text-slate-900">clear</button>
                ) : null}
              </p>
            </section>
          )}

          {/* Type picker — Fixed Bridge / Partial Denture / Full Denture */}
          {showProcs ? (
            <section>
              <p className="text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
                Type
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {CATEGORY_KEYS.map((key) => {
                  const def = CATEGORY_DEFS[key];
                  const cnt = countByCategory[key] || 0;
                  const active = categoryKey === key;
                  const empty  = cnt === 0;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => !empty && setCategoryKey(key)}
                      disabled={empty}
                      data-testid={`multi-tooth-category-${key}`}
                      className={`text-left px-3 py-2.5 rounded-lg border transition ${
                        active
                          ? "border-violet-500 bg-violet-50"
                          : empty
                            ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
                            : "border-slate-200 bg-white hover:border-slate-400"
                      }`}
                    >
                      <div className="text-sm font-semibold text-slate-900">{def.label}</div>
                      <div className={`text-[11px] mt-0.5 ${empty ? "text-amber-700" : "text-slate-500"}`}>
                        {empty ? "Add in Settings → Services" : def.desc}
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          ) : null}

          {/* Step 3 — For Fixed Bridge: crown type picker.
              For Partial / Full Denture: procedure picker. */}
          {showProcs && categoryKey === "bridge" ? (
            <section>
              <p className="text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
                Crown type
              </p>
              {crownProcs.length === 0 ? (
                <p className="text-xs text-slate-500 italic">
                  No crown procedures in this clinic's catalog. Add a Crown
                  procedure (PFM, Zirconia, E-Max, etc.) in Settings → Services.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {crownProcs.map((p) => {
                    const picked = crownProc?.id === p.id;
                    const totalForN = (p.default_fee_centavos || 0) * teethCount;
                    return (
                      <li key={p.id}>
                        <button type="button"
                                onClick={() => setCrownProc(p)}
                                data-testid={`multi-tooth-crown-${p.id}`}
                                className={`w-full px-3 py-2 rounded-lg border text-left text-sm flex items-center justify-between gap-3 ${
                                  picked
                                    ? "border-violet-500 bg-violet-50"
                                    : "border-slate-200 bg-white hover:border-slate-400"
                                }`}>
                          <span className="min-w-0">
                            <span className="font-semibold text-slate-900 truncate">{p.name}</span>
                            <span className="text-[11px] text-slate-500 ml-2">
                              {formatPHP(p.default_fee_centavos)} · per unit
                            </span>
                          </span>
                          <span className="text-xs tabular-nums text-slate-700 shrink-0">
                            × {teethCount} = <span className="font-semibold text-slate-900">{formatPHP(totalForN)}</span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          ) : null}

          {showProcs && categoryKey && categoryKey !== "bridge" ? (
            <section>
              <p className="text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
                Procedure
              </p>
              {procsInCategory.length === 0 ? (
                <p className="text-xs text-slate-500 italic">
                  No matching procedures in this clinic's catalog.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {procsInCategory.map((p) => {
                    const picked = pickedProcedure?.id === p.id;
                    return (
                      <li key={p.id}>
                        <button type="button"
                                onClick={() => setPickedProcedure(p)}
                                data-testid={`multi-tooth-proc-${p.id}`}
                                className={`w-full px-3 py-2 rounded-lg border text-left text-sm flex items-center justify-between gap-3 ${
                                  picked
                                    ? "border-violet-500 bg-violet-50"
                                    : "border-slate-200 bg-white hover:border-slate-400"
                                }`}>
                          <span className="min-w-0">
                            <span className="font-semibold text-slate-900 truncate">{p.name}</span>
                            <span className="text-[11px] text-slate-500 ml-2">
                              {categoryKey === "crown_units" ? `${formatPHP(p.default_fee_centavos)} · per unit` : p.category}
                            </span>
                            {(Number(p.visit_count) || 1) > 1 ? (
                              <span className="ml-2 inline-flex items-center text-[10px] tabular-nums px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">
                                ×{p.visit_count}
                              </span>
                            ) : null}
                          </span>
                          <span className="text-xs tabular-nums text-slate-700 shrink-0">
                            {categoryKey === "crown_units" && teethCount > 0
                              ? `× ${teethCount} = ${formatPHP((p.default_fee_centavos || 0) * teethCount)}`
                              : formatPHP(p.default_fee_centavos)}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          ) : null}

          {/* Step 3 — Role marker (Bridge etc) */}
          {pickedProcedure?.tooth_role_required ? (
            <section>
              <p className="text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
                Abutments + pontics
              </p>
              <div className="space-y-1.5">
                {[...selectedFdis]
                  .sort((a, b) => fdiToPosition(a) - fdiToPosition(b))
                  .map((fdi) => {
                  const role = roles[fdi];
                  const isMissing = missingFdiSet instanceof Set && missingFdiSet.has(String(fdi));
                  return (
                    <div key={fdi} className="flex items-center gap-2 text-sm">
                      {/* Fixed-width FDI slot — reserves the same column
                          space whether or not the "missing" pill renders,
                          so the Abutment / Pontic buttons stay aligned
                          across rows regardless of which teeth are
                          missing on the chart. */}
                      <div className="flex items-center gap-1.5 shrink-0 w-24">
                        <span
                          className={`font-semibold tabular-nums ${isMissing ? "line-through decoration-2 decoration-slate-400 text-slate-500" : "text-slate-900"}`}
                          title={isMissing ? "Missing on chart" : undefined}
                        >
                          #{fdi}
                        </span>
                        {isMissing ? (
                          <span
                            className="inline-flex items-center text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-600"
                            title="This tooth is flagged Missing on the patient's chart"
                          >
                            missing
                          </span>
                        ) : null}
                      </div>
                      <button type="button" onClick={() => setRole(fdi, "abutment")}
                              data-testid={`multi-tooth-role-${fdi}-abutment`}
                              className={`flex-1 px-3 py-1.5 rounded-lg border text-xs font-semibold ${
                                role === "abutment"
                                  ? "border-amber-500 bg-amber-50 text-amber-800"
                                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                              }`}>
                        Abutment
                      </button>
                      <button type="button" onClick={() => setRole(fdi, "pontic")}
                              data-testid={`multi-tooth-role-${fdi}-pontic`}
                              className={`flex-1 px-3 py-1.5 rounded-lg border text-xs font-semibold ${
                                role === "pontic"
                                  ? "border-violet-500 bg-violet-50 text-violet-800"
                                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                              }`}>
                        Pontic
                      </button>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Abutment / pontic is a clinical chart annotation only —
                doesn't change the fee.
              </p>
            </section>
          ) : null}
          </div>

          {/* PREVIEW PANE (right, 40% on desktop; stacks above on mobile) */}
          <aside className="lg:col-span-2 lg:order-last order-first">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 lg:sticky lg:top-4 space-y-3">
              <p className="text-[11px] font-bold tracking-wider uppercase text-slate-500">
                Preview
              </p>

              {!categoryKey ? (
                <p className="text-xs text-slate-500 italic">
                  Pick a procedure type on the left — the live preview shows here.
                </p>
              ) : (
                <>
                  {/* Type chip */}
                  <div className="flex items-center gap-2 text-xs">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-semibold">
                      {CATEGORY_DEFS[categoryKey].label}
                    </span>
                    {teethCount > 0 && categoryKey !== "full_denture" ? (
                      <span className="text-slate-500">
                        {teethCount} unit{teethCount === 1 ? "" : "s"}
                        {archLabel ? ` · ${archLabel}` : ""}
                      </span>
                    ) : null}
                  </div>

                  {/* Unit price chip (Crown Units only) */}
                  {categoryKey === "crown_units" && pickedProcedure ? (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">
                        {pickedProcedure.name}
                      </span>
                      <span className="text-slate-500 tabular-nums">
                        {formatPHP(pickedProcedure.default_fee_centavos)} per unit
                      </span>
                    </div>
                  ) : null}

                  {/* Material chip (Bridge only) */}
                  {categoryKey === "bridge" && crownProc ? (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">
                        {crownProc.name}
                      </span>
                      <span className="text-slate-500 tabular-nums">
                        {formatPHP(crownProc.default_fee_centavos)} per unit
                      </span>
                    </div>
                  ) : null}

                  {/* Role summary (Bridge only) */}
                  {needsRoles && allRoled ? (
                    <div className="text-[11px] text-slate-600 space-y-0.5">
                      <div>
                        Abutments:{" "}
                        <span className="font-semibold tabular-nums text-slate-900">
                          {[...selectedFdis]
                            .sort((a, b) => fdiToPosition(a) - fdiToPosition(b))
                            .filter((f) => roles[f] === "abutment").join(", ") || "—"}
                        </span>
                      </div>
                      <div>
                        Pontics:{" "}
                        <span className="font-semibold tabular-nums text-slate-900">
                          {[...selectedFdis]
                            .sort((a, b) => fdiToPosition(a) - fdiToPosition(b))
                            .filter((f) => roles[f] === "pontic").join(", ") || "—"}
                        </span>
                      </div>
                    </div>
                  ) : null}

                  {/* Fee in big type — clinical sentence below */}
                  <div className="border-t border-slate-200 pt-3">
                    <div className="text-2xl font-bold tabular-nums text-slate-900">
                      {Number.isFinite(computedFee) ? formatPHP(computedFee) : "—"}
                    </div>
                    <p className="text-xs text-slate-600 mt-1 leading-snug">
                      {clinicalSentence || "Live summary appears here."}
                    </p>
                  </div>
                </>
              )}
            </div>
          </aside>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-100 sticky bottom-0 bg-white z-10">
          <button type="button" onClick={() => onOpenChange?.(false)} disabled={submitting}
                  data-testid="multi-tooth-cancel"
                  className="px-3 py-2 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 bg-white hover:border-slate-400">
            Cancel
          </button>
          <div className="flex items-center gap-3 min-w-0">
            {disabledReason ? (
              <span className="text-[11px] text-slate-500 italic truncate" data-testid="multi-tooth-disabled-reason">
                {disabledReason}
              </span>
            ) : null}
            <button type="button" onClick={handleAdd}
                    disabled={!!disabledReason}
                    data-testid="multi-tooth-add"
                    className="px-4 py-2 text-sm font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              <span>
                Add to plan
                {!disabledReason && computedFee > 0 ? (
                  <span className="ml-1 opacity-90 tabular-nums">▸ {formatPHP(computedFee)}</span>
                ) : null}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Small inline tooth row for the mini picker.
function ToothRow({ fdis, selected, onToggle }) {
  return (
    <div className="flex items-center justify-center gap-1 flex-wrap">
      {fdis.map((fdi, i) => {
        // Visual midline divider after position 7 (between #11 and #21, etc.).
        const sep = i === 8 ? <span className="w-px h-6 bg-slate-300 mx-1.5" key={`sep-${fdi}`} aria-hidden /> : null;
        const isSel = selected.includes(fdi);
        return (
          <span key={fdi} className="inline-flex">
            {sep}
            <button
              type="button"
              onClick={() => onToggle(fdi)}
              data-testid={`multi-tooth-fdi-${fdi}`}
              className={`w-9 h-9 rounded-md border text-xs font-semibold tabular-nums transition ${
                isSel
                  ? "border-violet-600 bg-violet-600 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
              }`}
            >
              {fdi}
            </button>
          </span>
        );
      })}
    </div>
  );
}
